// =========================================
// db.firestore.js — store reattivo su Cloud Firestore
// =========================================
//
// Espone la STESSA API sincrona di db.local.js, ma i dati vivono su Firestore
// sotto  tenants/{tid}/{assets,locations,sensorInstances,movements}/{docId}.
//
// Come mantiene l'interfaccia sincrona che il resto dell'app si aspetta:
//   • una CACHE in memoria (`state`) rispecchia le collezioni del tenant;
//   • `ensureTenant(tid)` attacca un onSnapshot per collezione: ad ogni cambio
//     lato server la cache si riallinea e notifica i subscriber (le viste si
//     aggiornano "live");
//   • le letture (getCollection/getDocById) leggono la cache in O(1);
//   • le scritture aggiornano la cache in modo OTTIMISTICO + emit (UI reattiva
//     immediata) e propagano su Firestore in background; l'onSnapshot fa poi da
//     fonte di verità e corregge eventuali divergenze.
//
// I timestamp createdAt/updatedAt restano NUMERI (Date.now()) per compatibilità
// con gli helper di dominio che li confrontano come millisecondi.

import {
  collection, doc, onSnapshot, writeBatch, query, where, orderBy, limit as fbLimit, getDocs,
  setDoc as fbSetDoc, updateDoc as fbUpdateDoc, deleteDoc as fbDeleteDoc,
} from 'firebase/firestore';
import { db as firestore } from '../firebase/config';

const COLLECTIONS = ['assets', 'locations', 'sensorInstances', 'movements'];

let state = { tenants: {} };
const listeners = new Set();
// tid → { [coll]: unsubscribeFn } — listener onSnapshot attivi.
const snapUnsubs = {};

function emit() { listeners.forEach(fn => { try { fn(); } catch { /* ignore */ } }); }

function logErr(op) {
  return (err) => { console.error(`[firestore] ${op} fallita:`, err?.code || err?.message || err); };
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Attacca i listener onSnapshot per il tenant (idempotente).
export function ensureTenant(tenantId) {
  if (!tenantId) return;
  if (!state.tenants[tenantId]) {
    state.tenants[tenantId] = COLLECTIONS.reduce((acc, c) => { acc[c] = {}; return acc; }, {});
  }
  const fs = firestore();
  if (!fs || snapUnsubs[tenantId]) return; // già in ascolto (o SSR)

  snapUnsubs[tenantId] = {};
  for (const name of COLLECTIONS) {
    const ref = collection(fs, 'tenants', tenantId, name);
    snapUnsubs[tenantId][name] = onSnapshot(
      ref,
      (snap) => {
        const map = {};
        snap.forEach((d) => { map[d.id] = { id: d.id, ...d.data() }; });
        if (!state.tenants[tenantId]) state.tenants[tenantId] = {};
        state.tenants[tenantId][name] = map;
        emit();
      },
      (err) => {
        logErr(`onSnapshot ${name}`)(err);
        // Auto-risanamento: un permission-denied (es. email non ancora verificata)
        // lascerebbe listener falliti in snapUnsubs, bloccando il re-attach. Li
        // stacchiamo così un ensureTenant successivo (post-verifica) li riattacca.
        if (err?.code === 'permission-denied') detachTenant(tenantId);
      },
    );
  }
}

// Stacca i listener di un tenant (es. logout / cambio tenant).
export function detachTenant(tenantId) {
  const subs = snapUnsubs[tenantId];
  if (!subs) return;
  for (const name of Object.keys(subs)) { try { subs[name](); } catch { /* ignore */ } }
  delete snapUnsubs[tenantId];
}

function coll(tenantId, name) {
  if (!tenantId || !state.tenants[tenantId]) return {};
  if (!state.tenants[tenantId][name]) state.tenants[tenantId][name] = {};
  return state.tenants[tenantId][name];
}

// ── Reads (dalla cache) ───────────────────────────────────────────────────────
export function getCollection(tenantId, name) {
  return Object.values(coll(tenantId, name));
}

export function getDocById(tenantId, name, id) {
  return coll(tenantId, name)[id] || null;
}

// ── Writes (ottimistiche + propagazione Firestore) ────────────────────────────
export function setDoc(tenantId, name, id, data) {
  ensureTenant(tenantId);
  const c = state.tenants[tenantId][name] || (state.tenants[tenantId][name] = {});
  const prev = c[id] || {};
  const merged = { ...prev, ...data, id, createdAt: prev.createdAt || data.createdAt || Date.now(), updatedAt: Date.now() };
  c[id] = merged;
  emit();
  const fs = firestore();
  if (fs) fbSetDoc(doc(fs, 'tenants', tenantId, name, id), merged, { merge: true }).catch(logErr(`setDoc ${name}/${id}`));
  return merged;
}

export function updateDoc(tenantId, name, id, partial) {
  ensureTenant(tenantId);
  const c = state.tenants[tenantId][name] || (state.tenants[tenantId][name] = {});
  if (!c[id]) return null; // coerente con la semantica local: no-op se assente
  const now = Date.now();
  const merged = { ...c[id], ...partial, updatedAt: now };
  c[id] = merged;
  emit();
  const fs = firestore();
  if (fs) {
    fbUpdateDoc(doc(fs, 'tenants', tenantId, name, id), { ...partial, updatedAt: now })
      .catch(logErr(`updateDoc ${name}/${id}`));
  }
  return merged;
}

export function deleteDoc(tenantId, name, id) {
  const c = coll(tenantId, name);
  if (c[id]) { delete c[id]; emit(); }
  const fs = firestore();
  if (fs) fbDeleteDoc(doc(fs, 'tenants', tenantId, name, id)).catch(logErr(`deleteDoc ${name}/${id}`));
}


// Reset completo di un tenant: svuota cache e cancella i doc noti su Firestore.
export function resetTenant(tenantId) {
  const fs = firestore();
  const prev = state.tenants[tenantId] || {};
  if (fs) {
    const batch = writeBatch(fs);
    for (const name of COLLECTIONS) {
      for (const id of Object.keys(prev[name] || {})) {
        batch.delete(doc(fs, 'tenants', tenantId, name, id));
      }
    }
    batch.commit().catch(logErr('resetTenant'));
  }
  state.tenants[tenantId] = COLLECTIONS.reduce((acc, c) => { acc[c] = {}; return acc; }, {});
  emit();
}

// Storico misurazioni di un sensore (sotto-collezione `readings`, scritta dal cron
// syncSensorTelemetry). Ritorna le letture in ordine cronologico ASCendente.
//   opts.sinceMs — se >0, solo letture con ts >= sinceMs (filtro per periodo).
//   opts.max     — limite massimo di documenti.
export async function getReadings(tenantId, sensorId, { sinceMs = 0, max = 5000 } = {}) {
  const fs = firestore();
  if (!fs || !tenantId || !sensorId) return [];
  const ref = collection(fs, 'tenants', tenantId, 'sensorInstances', sensorId, 'readings');
  const parts = [];
  if (sinceMs > 0) parts.push(where('ts', '>=', sinceMs));
  parts.push(orderBy('ts', 'asc'));
  parts.push(fbLimit(max));
  const snap = await getDocs(query(ref, ...parts));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
